// MySQL Engine Extras — pure scanners and curated catalogs that enrich the
// MySQL resolver with dialect-specific intelligence:
//
//   • `findInsertNewAlias(source, statementStart, statementEnd)` — locates
//     the INSERT target + row alias (`INSERT … AS new ON DUPLICATE KEY
//     UPDATE new.col …`, MySQL 8.0.19+). The resolver uses the alias name
//     to map `new.<col>` to the target table's columns.
//   • `detectMatchAgainstContext(source, cursor, statementStart, statementEnd)`
//     — returns true when the cursor is inside the column-list parens of a
//     `MATCH (col1, col2 …) AGAINST (…)` expression. The resolver restricts
//     column emission to FULLTEXT-indexed columns in this context.
//   • `findUserVariables(source)` — buffer-level scan for `@varname`
//     references and `@varname := …` definitions. MySQL user variables are
//     session-scoped, so a buffer-wide scan is the right surface.
//   • `getMysqlJsonOperators()` — curated JSON operators surfaced at
//     expression-position contexts (`->`, `->>`).
//   • `getMysqlSystemVariables(opts)` — curated system variables surfaced
//     when the user types `@@`. Splits into MySQL-only / MariaDB-only /
//     shared sets so the variant gate selects the right list.
//   • `isMysqlSystemDatabase(name)` — case-insensitive membership check for
//     `information_schema`, `mysql`, `performance_schema`, `sys`. Drives
//     tier-8 demotion in `makeTableCandidate`.
//   • `isMariaDb(serverVersion)` — variant detector keyed off the
//     `MariaDB` substring in the version string.
//
// Pure module — no Monaco / electron imports. Operates on raw source text.

import { tokenize, type Token } from '../context/tokenizer';
import { parseIdentifierToken } from '../resolver/identifierUtils';

const ENGINE = 'mysql' as const;

// ─────────────────────────────────────────────────────────────────────────
// INSERT … AS <alias> ON DUPLICATE KEY UPDATE
// ─────────────────────────────────────────────────────────────────────────

export type MysqlInsertNewAlias = {
  readonly database?: string;
  readonly name: string;
  readonly alias: string;
};

/**
 * Find an `INSERT INTO target … VALUES (…) AS alias … ON DUPLICATE KEY UPDATE`
 * sequence inside the cursor's statement. Returns the target relation and the
 * row alias name. The MySQL 8.0.19+ row alias replaces the legacy
 * `VALUES(col)` references — `alias.col` resolves to the inserted row's
 * column, which is what completion should surface for `alias.<col>`.
 *
 * Returns null when the statement isn't an INSERT, or when no `AS alias`
 * follows VALUES, or when no `ON DUPLICATE KEY UPDATE` clause is present
 * (the alias is only meaningful in that context).
 */
export function findInsertNewAlias(
  source: string,
  statementStart: number,
  statementEnd: number,
): MysqlInsertNewAlias | null {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  let target: { database?: string; name: string } | null = null;
  let aliasName: string | null = null;
  let sawOnDuplicate = false;
  let lookForAs = false;

  for (let i = 0; i < tokens.length; i += 1) {
    const upper = identUpper(tokens[i]);
    if (!upper) continue;

    if (!target && upper === 'INSERT' && identUpper(tokens[i + 1]) === 'INTO') {
      const t = readQualifiedName(tokens, i + 2);
      if (t) target = t.target;
      continue;
    }

    // `VALUES (...)` followed by `AS <alias>` — also accept SET form
    // (`INSERT INTO t SET col = ... AS alias`) by setting `lookForAs` after
    // the first VALUES or SET keyword we observe.
    if (target && (upper === 'VALUES' || upper === 'VALUE' || upper === 'SET')) {
      lookForAs = true;
      continue;
    }

    if (target && lookForAs && !aliasName && upper === 'AS') {
      const next = tokens[i + 1];
      if (next && (next.type === 'ident' || next.type === 'kw')) {
        aliasName = parseIdentifierToken(next.text).content;
      }
      continue;
    }

    if (upper === 'ON' && identUpper(tokens[i + 1]) === 'DUPLICATE') {
      sawOnDuplicate = true;
    }
  }

  if (!target || !aliasName || !sawOnDuplicate) return null;
  return target.database
    ? { database: target.database, name: target.name, alias: aliasName }
    : { name: target.name, alias: aliasName };
}

// ─────────────────────────────────────────────────────────────────────────
// INSERT INTO target — bare target without row-alias
// ─────────────────────────────────────────────────────────────────────────

export type MysqlInsertTarget = {
  readonly database?: string;
  readonly name: string;
};

/**
 * Find the target relation of `INSERT [LOW_PRIORITY|DELAYED|HIGH_PRIORITY]
 * [IGNORE] INTO <target> …` (also `REPLACE INTO <target> …`) inside the
 * cursor's statement. Used to surface the target's columns inside the
 * column-list parens (`INSERT INTO t (|…)`) and inside the `ON DUPLICATE
 * KEY UPDATE` SET clause when no row-alias is declared. The legacy form
 * (no `AS new …`) is by far the dominant shape in the wild — the row-alias
 * path stays specialised in `findInsertNewAlias`.
 *
 * Returns null when the statement isn't an INSERT/REPLACE or no target is
 * parseable.
 */
export function findInsertTarget(
  source: string,
  statementStart: number,
  statementEnd: number,
): MysqlInsertTarget | null {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  for (let i = 0; i < tokens.length; i += 1) {
    const upper = identUpper(tokens[i]);
    if (!upper) continue;
    // Both INSERT and REPLACE share the column-list / SET / ON DUPLICATE
    // shape; surface the same target either way.
    if (upper !== 'INSERT' && upper !== 'REPLACE') continue;
    // Walk forward over INSERT modifiers (`LOW_PRIORITY`, `DELAYED`,
    // `HIGH_PRIORITY`, `IGNORE`) until we hit `INTO`. `INTO` is optional
    // for INSERT/REPLACE — accept the immediate-target form too.
    let j = i + 1;
    while (j < tokens.length) {
      const u = identUpper(tokens[j]);
      if (u === 'INTO') {
        j += 1;
        break;
      }
      if (
        u === 'LOW_PRIORITY' ||
        u === 'DELAYED' ||
        u === 'HIGH_PRIORITY' ||
        u === 'IGNORE'
      ) {
        j += 1;
        continue;
      }
      // First non-modifier token — must be the target identifier.
      break;
    }
    const parsed = readQualifiedName(tokens, j);
    if (!parsed) continue;
    return parsed.target.database
      ? { database: parsed.target.database, name: parsed.target.name }
      : { name: parsed.target.name };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────
// MATCH (…) AGAINST (…) detection
// ─────────────────────────────────────────────────────────────────────────

/**
 * Return true when `cursor` sits inside the column-list parens of a
 * `MATCH (col1, col2 …) AGAINST (…)` expression. Source-text walk —
 * tree-sitter MySQL grammar parks MATCH inside a function-call shape that
 * is awkward to reach via tree walking, so we use a cheap forward scan
 * over tokens within the cursor's statement.
 *
 * Detection rule: walk tokens; when we see the keyword `MATCH` followed by
 * `(`, capture the matching `)` offset. If `cursor` falls in `(matchOpen,
 * matchClose]`, the cursor is in the column-list slot. AGAINST may follow
 * after the match but its presence isn't required for the predicate (the
 * user could be typing the cols list before AGAINST).
 */
export function detectMatchAgainstContext(
  source: string,
  cursor: number,
  statementStart: number,
  statementEnd: number,
): boolean {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  for (let i = 0; i < tokens.length; i += 1) {
    const upper = identUpper(tokens[i]);
    if (upper !== 'MATCH') continue;
    const lparen = tokens[i + 1];
    if (!lparen || lparen.type !== 'lparen') continue;
    const closeIdx = findMatchingRparen(tokens, i + 1);
    if (closeIdx === -1) {
      // Unclosed MATCH paren — cursor inside (or right after `(`) still
      // counts. `>= lparen.end` catches the boundary case where the cursor
      // is parked immediately after `(` with nothing typed yet.
      if (cursor >= lparen.end) return true;
      continue;
    }
    const closeTok = tokens[closeIdx]!;
    if (cursor >= lparen.end && cursor <= closeTok.start) return true;
  }
  return false;
}

/**
 * True when the cursor sits in the ALIAS LIST of MySQL's multi-table DELETE —
 * `DELETE ▮r, p FROM rental r JOIN payment p ON …`.
 *
 * MySQL-ONLY syntax, deliberately not mirrored: standard `DELETE FROM t` is
 * what every other engine has, and there the token after `DELETE` is the
 * keyword `FROM`, never a relation reference. `USING`-form multi-table
 * DELETE (`DELETE FROM t1 USING …`) is a different slot — the names there
 * follow `FROM`, so this scanner does not claim it.
 *
 * Detection rule: the cursor's statement starts with `DELETE`, and no `FROM`
 * token appears between that keyword and the cursor. `QUICK` / `LOW_PRIORITY`
 * / `IGNORE` are the modifiers MySQL allows between `DELETE` and the list, so
 * they are skipped rather than ending the slot. Everything after the first
 * `FROM` is the ordinary relation list, which the `from` arm owns.
 */
export function isMysqlMultiTableDeleteAliasSlot(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const tokens = tokenize(source, statementStart, cursor, ENGINE);
  if (tokens.length === 0) return false;
  if (identUpper(tokens[0]) !== 'DELETE') return false;
  // A partially typed alias is still in the slot, so the trailing identifier
  // token is not evidence of anything; only a `FROM` closes the slot.
  for (let i = 1; i < tokens.length; i += 1) {
    const upper = identUpper(tokens[i]);
    if (upper === 'FROM') return false;
    // A paren or subquery here is not the alias list.
    if (tokens[i]!.type === 'lparen') return false;
  }
  return true;
}

function findMatchingRparen(tokens: readonly Token[], lparenIdx: number): number {
  let depth = 0;
  for (let j = lparenIdx; j < tokens.length; j += 1) {
    if (tokens[j].type === 'lparen') depth += 1;
    else if (tokens[j].type === 'rparen') {
      depth -= 1;
      if (depth === 0) return j;
    }
  }
  return -1;
}

// ─────────────────────────────────────────────────────────────────────────
// User variables — buffer-wide scan
// ─────────────────────────────────────────────────────────────────────────

/**
 * Scan the entire buffer for MySQL user variable references. Returns the
 * unique list of variable names (without the `@` prefix). Catches both
 * `SET @x = …` / `SELECT @x := …` definitions and `@x` reads.
 *
 * MySQL user variables are session-scoped and survive across statements
 * within the same connection, so a buffer-wide scan is the right surface
 * for completion — the user is editing one continuous script and any
 * variable they declare earlier in the buffer should surface later.
 *
 * Handles strings (`'…'` with `''`-escape, double-quoted strings, backtick
 * identifiers) so an `@x` inside a string literal is ignored.
 */
export function findUserVariables(source: string): readonly string[] {
  const out = new Set<string>();
  let i = 0;
  while (i < source.length) {
    const ch = source[i]!;

    // Skip strings, comments, quoted-identifier forms.
    if (ch === "'") {
      i = skipSingleQuoted(source, i);
      continue;
    }
    if (ch === '"') {
      i = skipDoubleQuoted(source, i);
      continue;
    }
    if (ch === '`') {
      i = skipBacktick(source, i);
      continue;
    }
    if (ch === '-' && source[i + 1] === '-') {
      i = skipToEol(source, i + 2);
      continue;
    }
    if (ch === '#') {
      i = skipToEol(source, i + 1);
      continue;
    }
    if (ch === '/' && source[i + 1] === '*') {
      i = skipBlockComment(source, i + 2);
      continue;
    }

    if (ch === '@') {
      // Skip `@@` (system variables — handled separately).
      if (source[i + 1] === '@') {
        i += 2;
        continue;
      }
      // Read user-var name.
      const start = i + 1;
      let end = start;
      while (end < source.length && isUserVarChar(source.charCodeAt(end))) end += 1;
      if (end > start) {
        out.add(source.slice(start, end));
      }
      i = end > start ? end : i + 1;
      continue;
    }

    i += 1;
  }
  return Array.from(out).sort();
}

function isUserVarChar(c: number): boolean {
  // MySQL user-variable name chars: letters, digits, `_`, `$`, `.`.
  // We stop at `.` to keep the simple non-dotted form; full path-form vars
  // (`@my.scope.x`) are rare and out of scope for this slice.
  return (
    (c >= 0x41 && c <= 0x5a) ||
    (c >= 0x61 && c <= 0x7a) ||
    (c >= 0x30 && c <= 0x39) ||
    c === 0x5f ||
    c === 0x24
  );
}

function skipSingleQuoted(source: string, from: number): number {
  let i = from + 1;
  while (i < source.length) {
    const ch = source[i]!;
    if (ch === "'") {
      if (source[i + 1] === "'") {
        i += 2;
        continue;
      }
      return i + 1;
    }
    if (ch === '\\' && i + 1 < source.length) {
      i += 2;
      continue;
    }
    i += 1;
  }
  return source.length;
}

function skipDoubleQuoted(source: string, from: number): number {
  let i = from + 1;
  while (i < source.length) {
    const ch = source[i]!;
    if (ch === '"') {
      if (source[i + 1] === '"') {
        i += 2;
        continue;
      }
      return i + 1;
    }
    if (ch === '\\' && i + 1 < source.length) {
      i += 2;
      continue;
    }
    i += 1;
  }
  return source.length;
}

function skipBacktick(source: string, from: number): number {
  let i = from + 1;
  while (i < source.length) {
    const ch = source[i]!;
    if (ch === '`') {
      if (source[i + 1] === '`') {
        i += 2;
        continue;
      }
      return i + 1;
    }
    i += 1;
  }
  return source.length;
}

function skipToEol(source: string, from: number): number {
  let i = from;
  while (i < source.length && source[i] !== '\n') i += 1;
  return i;
}

function skipBlockComment(source: string, from: number): number {
  let i = from;
  while (i + 1 < source.length) {
    if (source[i] === '*' && source[i + 1] === '/') return i + 2;
    i += 1;
  }
  return source.length;
}

// ─────────────────────────────────────────────────────────────────────────
// Operator catalogs
// ─────────────────────────────────────────────────────────────────────────

export type MysqlOperatorEntry = {
  readonly op: string;
  readonly description: string;
};

const JSON_OPERATORS: readonly MysqlOperatorEntry[] = [
  { op: '->', description: 'JSON: extract value (returns JSON)' },
  { op: '->>', description: 'JSON: extract value and unquote (returns text)' },
];

export function getMysqlJsonOperators(): readonly MysqlOperatorEntry[] {
  return JSON_OPERATORS;
}

// ─────────────────────────────────────────────────────────────────────────
// System variables (`@@global.x`, `@@session.x`, `@@x`)
// ─────────────────────────────────────────────────────────────────────────

export type MysqlSystemVariable = {
  readonly name: string;
  readonly description: string;
  readonly mariadbOnly?: boolean;
  readonly mysqlOnly?: boolean;
};

/**
 * Curated subset of frequently-used MySQL system variables. Splits into
 * MySQL-only / MariaDB-only / shared sets so the resolver can filter via
 * the variant detector. The full list of system variables runs into the
 * hundreds; this curated set covers the common operational + query-tuning
 * surface (charset, isolation, sizes, version, time zone, sql_mode, …).
 */
const SYSTEM_VARIABLES: readonly MysqlSystemVariable[] = [
  { name: 'autocommit', description: 'Automatic commit mode' },
  { name: 'character_set_client', description: 'Character set for client → server' },
  { name: 'character_set_connection', description: 'Character set for current connection' },
  { name: 'character_set_database', description: 'Default database character set' },
  { name: 'character_set_results', description: 'Character set for query results' },
  { name: 'character_set_server', description: 'Server default character set' },
  { name: 'collation_connection', description: 'Collation for current connection' },
  { name: 'collation_database', description: 'Default database collation' },
  { name: 'collation_server', description: 'Server default collation' },
  { name: 'foreign_key_checks', description: 'FK constraint enforcement toggle' },
  { name: 'group_concat_max_len', description: 'Max length of GROUP_CONCAT result' },
  { name: 'innodb_buffer_pool_size', description: 'InnoDB buffer pool size (bytes)' },
  { name: 'innodb_lock_wait_timeout', description: 'InnoDB lock wait timeout (seconds)' },
  { name: 'lower_case_table_names', description: 'Table-name case sensitivity (0/1/2)' },
  { name: 'max_allowed_packet', description: 'Max query packet size (bytes)' },
  { name: 'max_connections', description: 'Maximum simultaneous connections' },
  { name: 'sql_mode', description: 'SQL mode flags (e.g. ANSI_QUOTES)' },
  { name: 'sql_safe_updates', description: 'Block UPDATE/DELETE without WHERE+key' },
  { name: 'time_zone', description: 'Session / server time zone' },
  { name: 'tx_isolation', description: 'Transaction isolation level' },
  { name: 'unique_checks', description: 'Unique-index enforcement toggle' },
  { name: 'version', description: 'Server version string' },
  { name: 'version_comment', description: 'Server version comment / build name' },
  { name: 'wait_timeout', description: 'Idle connection timeout (seconds)' },
  // MySQL-specific.
  { name: 'optimizer_switch', description: 'Optimizer flag toggles', mysqlOnly: true },
  { name: 'transaction_isolation', description: 'Transaction isolation (8.0+)', mysqlOnly: true },
  { name: 'optimizer_trace', description: 'Optimizer trace control', mysqlOnly: true },
  { name: 'binlog_format', description: 'Binary log format' },
  // MariaDB-specific.
  { name: 'aria_log_file_size', description: 'Aria storage engine log file size', mariadbOnly: true },
  { name: 'aria_pagecache_buffer_size', description: 'Aria page-cache buffer', mariadbOnly: true },
  { name: 'maria_log_record_size_limit', description: 'Aria log record size limit', mariadbOnly: true },
  { name: 'oqgraph_allow_create_integer_latch', description: 'OQGRAPH integer latch', mariadbOnly: true },
];

export function getMysqlSystemVariables(opts: {
  mariadb: boolean;
}): readonly MysqlSystemVariable[] {
  return SYSTEM_VARIABLES.filter((v) => {
    if (opts.mariadb && v.mysqlOnly) return false;
    if (!opts.mariadb && v.mariadbOnly) return false;
    return true;
  });
}

// ─────────────────────────────────────────────────────────────────────────
// System database + variant detector
// ─────────────────────────────────────────────────────────────────────────

const SYSTEM_DATABASES = new Set(['information_schema', 'mysql', 'performance_schema', 'sys']);

/**
 * Tier-8 demotion check for MySQL system databases. The four system DBs
 * `information_schema`, `mysql`, `performance_schema`, `sys` carry catalog
 * + maintenance tables that belong at the bottom of the suggestion list —
 * useful for power users, noisy for regular completion.
 */
export function isMysqlSystemDatabase(name: string | undefined): boolean {
  if (!name) return false;
  return SYSTEM_DATABASES.has(name.toLowerCase());
}

/**
 * The four MySQL system databases, surfaced at FROM position as tier-8
 * completions so users can write three-part references (e.g.
 * `information_schema.tables`, `mysql.user`) without typing the database
 * name from memory. `mysqlSchemaIntrospection.fetchDatabases` filters these
 * out of the regular `databases` list so they don't pollute tier-4 ranking;
 * this list keeps them available at the bottom of the suggestion stack.
 */
export const MYSQL_SYSTEM_DATABASES: readonly string[] = [
  'information_schema',
  'mysql',
  'performance_schema',
  'sys',
];

/**
 * MariaDB variant detector. Uses the `MariaDB` substring in the version
 * string (`'10.11.6-MariaDB'`, `'10.5.21-MariaDB-log'`, etc.). MySQL's
 * `VERSION()` returns plain numerics like `'8.0.34'` without the substring.
 *
 * Future MariaDB-specific feature gates (e.g. `RETURNING` for INSERT/DELETE
 * since 10.5, `JSON_VALUE … RETURNING` since 10.6) should consult this
 * helper rather than threading a `variant` field through the resolver.
 */
export function isMariaDb(serverVersion: string | undefined): boolean {
  if (!serverVersion) return false;
  return /MariaDB/i.test(serverVersion);
}

// ─────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────

function readQualifiedName(
  tokens: readonly Token[],
  startIdx: number,
): { target: { database?: string; name: string }; lastIdx: number } | null {
  if (startIdx >= tokens.length) return null;
  const head = tokens[startIdx];
  if (!head || (head.type !== 'ident' && head.type !== 'kw')) return null;
  const parts: string[] = [parseIdentifierToken(head.text).content];
  let i = startIdx + 1;
  while (i < tokens.length && tokens[i].type === 'dot') {
    const next = tokens[i + 1];
    if (!next || (next.type !== 'ident' && next.type !== 'kw')) break;
    parts.push(parseIdentifierToken(next.text).content);
    i += 2;
  }
  const name = parts[parts.length - 1]!;
  const database = parts.length >= 2 ? parts[parts.length - 2] : undefined;
  return { target: database ? { database, name } : { name }, lastIdx: i - 1 };
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
